import React, { ReactElement } from "react";
import { Icon, ListTableColumn, Text, renderEnumValue, Stack, Label } from "sprint-common-web";

import { CloudFileStatus } from "../types/CloudFileStatus";
import { CloudFileStatusDetails } from "../types/CloudFileStatusDetails";

const renderStatusDetailsTooltip = (segments: string[], testId: string): JSX.Element | undefined => {
    if (segments.length === 0 || segments[0] === "-") {
        return undefined;
    }

    return (
        <Stack data-testid={testId}>
            {segments.map((segment, index) => (
                <React.Fragment key={index}>
                    {index > 0 && <br />}
                    {segment}
                </React.Fragment>
            ))}
        </Stack>
    );
};

export const renderCloudFileStatus = <T extends { statusDetails?: CloudFileStatusDetails }>(
    item?: T,
    _index?: number,
    column?: ListTableColumn<T>
): ReactElement | null => {
    const segments = renderCloudFileStatusDetailsData(item?.statusDetails);
    return (
        <Text
            variant={"small"}
            styles={{ root: { cursor: item?.statusDetails ? "pointer" : "default" } }}
            tooltip={renderStatusDetailsTooltip(segments, "cloud-file-status-cell-tooltip-text")}
            testId="cloud-file-status-cell"
        >
            {renderEnumValue(CloudFileStatus, item, _index, column)}
            {item?.statusDetails && <Icon iconName="Info" size={11} padding="0 0 0 2px" />}
        </Text>
    );
};

export const renderCloudFileStatusDetailsText = (statusDetails?: CloudFileStatusDetails): JSX.Element => {
    const segments = renderCloudFileStatusDetailsData(statusDetails);
    let statusDetailsTextView = (
        <Text
            styles={{ root: { cursor: statusDetails ? "pointer" : "default" } }}
            maxLength={40}
            tooltip={renderStatusDetailsTooltip(segments, "status-details-field-tooltip-text")}
            testId="status-details-field-tooltip"
        >
            {segments[0]}
            {statusDetails && <Icon iconName="Info" size={11} padding="0 0 0 2px" />}
        </Text>
    );

    if (!statusDetails || Object.keys(statusDetails).length === 0) {
        statusDetailsTextView = <Text>-</Text>;
    }

    return (
        <Stack data-testId="status-details-field" styles={{ root: { marginBottom: 10 } }}>
            <Label data-testId="status-details-field-label">Status details</Label>
            <Stack styles={{ root: { display: "flex", flexFlow: "row" } }}>{statusDetailsTextView}</Stack>
        </Stack>
    );
};

export const renderCloudFileStatusDetailsData = (statusDetails?: CloudFileStatusDetails): string[] => {
    if (!statusDetails) {
        return ["-"];
    }

    const parts: string[] = [];

    const firstLine = [statusDetails.reason, statusDetails.details].filter(Boolean).join(": ");
    if (firstLine) {
        parts.push(firstLine);
    }

    const secondLine = [statusDetails.courseOfAction, statusDetails.fixedBilling].filter(Boolean).join(": ");
    if (secondLine) {
        parts.push(secondLine);
    }

    return parts.length > 0 ? parts : ["-"];
};
